import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  createHttpLink,
  fromPromise,
  InMemoryCache,
  Observable,
  type FetchResult,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import { tokenStorage } from "@/modules/auth";
import { getOrStartRefresh } from "@/modules/auth/sessionRefresh";
import type { Tokens } from "@/modules/auth/tokenStorage";
import config from "../../config";
import { showErrorNotification } from "../../utils";
import { getServerErrorNotification } from "../../utils/error";
import {
  isExpectedGraphQlError,
  rememberGraphQlOperation,
} from "./graphQlErrorStatus";
import { CustomSentryLink } from "./sentryLink";

const authLink = setContext((_, { headers }) => {
  const accessToken = tokenStorage.getAccessToken();
  if (!accessToken) {
    return { headers };
  }
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${accessToken}`,
    },
  };
});

/*
 * Remembers which operation an error belongs to. It sits right before the
 * HTTP link, so the replay the error link forwards after a token refresh
 * passes through it again; the error link's own handler never sees the
 * outcome of that replay.
 */
const operationNameLink = new ApolloLink((operation, forward) => {
  const { operationName } = operation;
  const observable = forward(operation);
  if (!operationName) {
    return observable;
  }
  return new Observable<FetchResult>((observer) => {
    const subscription = observable.subscribe({
      next: (result) => {
        result.errors?.forEach((graphQlError) =>
          rememberGraphQlOperation(graphQlError, operationName)
        );
        observer.next(result);
      },
      error: (networkError: unknown) => {
        if (typeof networkError === "object" && networkError !== null) {
          rememberGraphQlOperation(networkError, operationName);
        }
        observer.error(networkError);
      },
      complete: () => observer.complete(),
    });
    return () => subscription.unsubscribe();
  });
});

type ErrorLinkArgs = Parameters<Parameters<typeof onError>[0]>[0];

/*
 * Tells the operator that a query failed. Mutations stay silent: their call
 * sites report their own failures.
 */
const notifyQueryFailure = (
  operation: ErrorLinkArgs["operation"],
  graphQLErrors: ErrorLinkArgs["graphQLErrors"],
  networkError: ErrorLinkArgs["networkError"]
) => {
  const operationDefinition = operation.query.definitions.find(
    ({ kind }) => kind === "OperationDefinition"
  );
  if (
    operationDefinition &&
    "operation" in operationDefinition &&
    operationDefinition.operation === "query" &&
    (graphQLErrors || networkError)
  ) {
    // There was an error while retrieving some information
    const genericMessage =
      "Beim Abrufen einiger Informationen ist ein Fehler aufgetreten";
    const error = new ApolloError({
      graphQLErrors,
      networkError,
    });
    // An expected 4xx carries a message meant for the operator
    showErrorNotification(
      isExpectedGraphQlError(error)
        ? getServerErrorNotification(error, genericMessage)
        : { message: genericMessage }
    );
  }
};

/*
 * The error link's own handler never sees the outcome of the replay it
 * forwards after a token refresh, so the replay reports its failure itself.
 * A partial HTTP 500 emits the errors and then fails, hence the single shot.
 */
const notifyReplayFailure = (
  operation: ErrorLinkArgs["operation"],
  replay: Observable<FetchResult>
) =>
  new Observable<FetchResult>((observer) => {
    let notified = false;
    const notifyOnce = (
      graphQLErrors: ErrorLinkArgs["graphQLErrors"],
      networkError: ErrorLinkArgs["networkError"]
    ) => {
      if (!notified) {
        notified = true;
        notifyQueryFailure(operation, graphQLErrors, networkError);
      }
    };
    const subscription = replay.subscribe({
      next: (result) => {
        if (result.errors) {
          notifyOnce(result.errors, undefined);
        }
        observer.next(result);
      },
      error: (networkError: ErrorLinkArgs["networkError"]) => {
        const resultErrors =
          networkError && "result" in networkError
            ? (
                networkError.result as {
                  errors?: ErrorLinkArgs["graphQLErrors"];
                }
              )?.errors
            : undefined;
        notifyOnce(resultErrors, networkError);
        observer.error(networkError);
      },
      complete: () => observer.complete(),
    });
    return () => subscription.unsubscribe();
  });

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    const status =
      networkError && "statusCode" in networkError
        ? networkError.statusCode
        : undefined;

    if (status === 401) {
      return fromPromise(
        getOrStartRefresh().catch((): Tokens | null => null)
      ).flatMap((tokens: Tokens | null) => {
        if (!tokens) {
          /*
           * Refresh failed — surface the ORIGINAL 401 to the caller instead
           * of replaying the operation with the stale token (Apollo's
           * `forward(operation)` dispatches a new request, which would 401
           * again and double-pay the round-trip). The session-expired modal
           * is already wired via the sessionEvents bus.
           */
          return new Observable<FetchResult>((observer) => {
            observer.error(networkError ?? new Error("Session expired"));
          });
        }
        const context = operation.getContext();
        operation.setContext({
          headers: {
            ...context.headers,
            authorization: `Bearer ${tokens.accessToken}`,
          },
        });
        return notifyReplayFailure(operation, forward(operation));
      });
    }

    notifyQueryFailure(operation, graphQLErrors, networkError);
  }
);

export interface CreateGraphqlClientOptions {
  fetch?: typeof fetch;
}

export const createGraphqlClient = ({
  fetch: fetchImpl,
}: CreateGraphqlClientOptions = {}): ApolloClient<unknown> => {
  const httpLink = createHttpLink({
    uri: `${config.VITE_APP_API_URL}/api/kontax/graphql`,
    ...(fetchImpl ? { fetch: fetchImpl } : {}),
  });

  return new ApolloClient({
    link: ApolloLink.from([
      new CustomSentryLink({
        attachBreadcrumbs: {
          includeError: true,
          includeQuery: true,
          includeVariables: true,
        },
      }),
      authLink,
      errorLink,
      operationNameLink,
      httpLink,
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      // Cache should be explicitly enabled per query
      query: { fetchPolicy: "no-cache" },
      watchQuery: { fetchPolicy: "no-cache" },
    },
  });
};

const client = createGraphqlClient();
export default client;
